import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react'
import useScript from 'react-script-hook'
import Skeleton from 'react-loading-skeleton'

import { useReservationLocations } from 'shared/js/hooks/useReservationLocations'
import Button from 'src/shared/components/common/button'
import DropDown from 'shared/components/common/dropdown'
import { DropDownOption } from 'shared/components/common/dropdown/DropDownOption'
import { formatAddress } from 'shared/components/common/form_input/LocationInput'
import { LocationIcon } from '@toasttab/buffet-pui-icons'
import { useSelectReservationLocation } from './useSelectReservationLocation'
import { resources } from 'config'
import { t } from 'src/translations'
import { reportError } from 'src/lib/js/clientError'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useCookieConsentContext } from 'src/public/js/cookie_consent/CookieConsentContext'
import { getSessionManager } from 'src/lib/js/guest-analytics/sessionManager'

import config from 'config'
import { useEditor } from '@toasttab/sites-components'
import {
  ModalCloseButton,
  useModalContext
} from 'shared/components/common/modal'
import classNames from 'classnames'

declare global {
  interface Window {
    ToastBookingWidget?: {
      open: (params?: {
        _toastSitesWebAnalytics?: {
          siteGuid?: string
          sessionGuid?: string
          source: string
        }
      }) => void
    }
  }
}

const DefaultLocationLabel = () => {
  return (
    <div className='reservation-default-location-label'>
      <LocationIcon accessibility='decorative' />{' '}
      {t('reservation.select-a-location')}
    </div>
  )
}

type ReservationFormV2Props = {
  isModal?: Boolean
}

/**
 * Reservation component which opens the Reservations iframe
 */
export const ReservationFormV2 = ({
  isModal = false
}: ReservationFormV2Props) => {
  const { isEditor } = useEditor()
  const { onClose } = useModalContext()
  const reservationEnabledLocations = useReservationLocations()
  const { restaurant } = useRestaurant()
  const { analyticsConsent } = useCookieConsentContext()
  const isProdEnv = config.environment === 'production'

  // Ref to track timeout for cleanup
  const onCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // State to prevent multiple submissions
  const [isSubmitting, setIsSubmitting] = useState(false)

  const options = useMemo(
    () =>
      reservationEnabledLocations?.map((location) => ({
        label: location?.name || '',
        value: location?.externalId || ''
      })) || [],
    [reservationEnabledLocations]
  )

  // Locations are still loading when the query hasn't resolved yet
  const isLoadingLocations = reservationEnabledLocations === undefined
  const singleLocation =
    reservationEnabledLocations?.length === 1
      ? reservationEnabledLocations[0]
      : undefined

  // Auto-select location if only one option is available
  const { selectedLocation, setSelectedLocation, dropdownDisabled } =
    useSelectReservationLocation(options)

  // Only load script when a location is selected
  const [loading, error] = useScript({
    src: selectedLocation.value ? resources.bookingScriptUrl : '', // Empty string prevents script from loading
    'data-restaurant-guid': selectedLocation.value || '',
    'data-env': isProdEnv ? 'production' : 'preprod',
    strategy: 'afterInteractive',
    checkForExisting: true
  })

  // Automatically redirect to fallback URL when script fails to load
  useEffect(() => {
    if (error && selectedLocation.value) {
      reportError(`Booking widget failed to load: ${error}`)
      // Redirect to fallback reservation URL
      window.location.href = `${resources.reservationFallbackUrl}/${selectedLocation.value}/reserve`
    }
  }, [error, selectedLocation.value])

  /**
   * The tables widget sends a postMessage that the iframe closed
   * when that happens we want to update isSubmitting to false
   * so that the Check Availibility button is enabled again so that
   * users can interact with it
   */
  useEffect(() => {
    if (isEditor) return
    const reservationWidgetCloseEventListener = (event: MessageEvent) => {
      if (event.origin !== new URL(resources.bookingScriptUrl).origin) return
      if (event.data.type === 'CLOSE_AVAILABILITIES_MODAL') {
        setIsSubmitting(false)
      }
    }
    window.addEventListener('message', reservationWidgetCloseEventListener)
    return () => {
      window.removeEventListener('message', reservationWidgetCloseEventListener)
    }
  }, [])

  const handleSubmit = useCallback(() => {
    // Prevent multiple clicks
    if (isSubmitting) return

    setIsSubmitting(true)

    if (window?.ToastBookingWidget) {
      window.ToastBookingWidget.open({
        _toastSitesWebAnalytics: {
          siteGuid: restaurant?.id ?? undefined,
          sessionGuid: analyticsConsent
            ? getSessionManager().getSessionGuid()
            : undefined,
          source: 'website_integrated_module'
        }
      })
    }

    // Need a timeout here to prevent showing our modal closing before the
    // reservations widget modal opens
    if (isModal && onClose) {
      onCloseTimeoutRef.current = setTimeout(() => {
        onClose()
        onCloseTimeoutRef.current = null
      }, 1000)
    }
  }, [isModal, onClose, isSubmitting, restaurant?.id, analyticsConsent])

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (onCloseTimeoutRef.current) {
        clearTimeout(onCloseTimeoutRef.current)
        onCloseTimeoutRef.current = null
      }
    }
  }, [])

  return (
    <div
      className={classNames('reservation-form-v2', { isModal })}
      data-testid='reservation-form-v2'
    >
      {isModal && <ModalCloseButton className='reservationModalCloseButton' />}
      <h2>{t('reservation.reservations')}</h2>
      {isLoadingLocations ? (
        <>
          <Skeleton
            containerClassName='descriptionSkeleton'
            height={16}
            width='90%'
          />
          <Skeleton
            containerClassName='location-selector-skeleton'
            height={48}
          />
        </>
      ) : singleLocation ? (
        <p className='description'>
          {t('reservation.make-reservation-single-location', {
            name: singleLocation.name,
            address: formatAddress(singleLocation)
          })}
        </p>
      ) : (
        <>
          <p className='description'>{t('reservation.pick-your-location')}</p>
          <DropDown
            className='location-selector'
            label={selectedLocation.label || <DefaultLocationLabel />}
            testId='reservation-form-v2-dropdown'
            ariaDescription='select a location to make a reservation'
            disabled={dropdownDisabled}
          >
            {({ close }) =>
              options.map((option) => (
                <DropDownOption
                  key={option.value}
                  onSelect={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setSelectedLocation({
                      label: option.label,
                      value: option.value
                    })
                    close()
                  }}
                  onClose={close}
                >
                  {option.label}
                </DropDownOption>
              ))
            }
          </DropDown>
        </>
      )}
      <div className='cta-button-container'>
        <Button
          disabled={
            !isEditor &&
            (!selectedLocation.value ||
              loading ||
              !window?.ToastBookingWidget ||
              isSubmitting)
          }
          onClick={isEditor ? undefined : handleSubmit}
          type='submit'
          style={isEditor ? { pointerEvents: 'none', cursor: 'default' } : {}}
        >
          {t('reservation.check-availability')}
        </Button>
      </div>
    </div>
  )
}
